import React from "react"
import { Grid, TextField, Typography } from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab"

import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"
import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"

import { formatSaveInputLocationOptions, SaveInputLocationData } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"

import { ChatBotFlowBlockContent, SuperlogicaSearchField } from "@/protocols/chatBot"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/SuperlogicaLookupContactBlock/SearchSectionEditor/styles"

type SearchSectionEditorProps = {
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
	chatBotFlowBlockContent: ChatBotFlowBlockContent
}

const searchFieldOptions: {
	value: SuperlogicaSearchField
	label: string
}[] = [
	{ value: "condominiumName", label: "Nome do Condomínio" }
]

const SearchSectionEditor: React.FC<SearchSectionEditorProps> = ({
	chatBotFlowBlockContent,
	onSave,
	children
}) => {
	const contentEditor = useContentEditor({ chatBotFlowBlockContent })
	const superlogicaBlock = contentEditor.content?.superlogicaBlock
	const classes = useStyles()
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()

	const customFieldOptions = formatSaveInputLocationOptions(chatBotFlowConstructorStore.constructionResources)
		.filter(option => option.type === "custom-field")

	const handleChangeSearchCustomField = (field: SuperlogicaSearchField, selected: SaveInputLocationData | null) => {
		const prevSearch = superlogicaBlock?.search || []
		const searchWithoutField = prevSearch.filter(item => item.field !== field)

		const nextSearch = selected
			? [...searchWithoutField, { field, customFieldId: selected.customFieldId }]
			: searchWithoutField

		contentEditor.changeContent({
			superlogicaBlock: {
				...superlogicaBlock,
				search: nextSearch
			}
		})
	}

	const handleOpen = () => contentEditor.reset()
	const handleSave = () => onSave(contentEditor.content)

	return (
		<BlockBodySectionEditor
			title="Buscar contato"
			onSave={handleSave}
			onOpen={handleOpen}
			AsideDrawerProps={{
				children: (
					<Grid container direction="column" spacing={2}>
						<Grid item xs={12}>
							<Grid container direction="column" spacing={3}>
								{searchFieldOptions.map(fieldOption => {
									const savedCustomFieldId = superlogicaBlock?.search?.find(item => item.field === fieldOption.value)?.customFieldId
									const selectedCustomField = customFieldOptions.find(option => option.customFieldId === savedCustomFieldId) || null

									return (
										<Grid item xs={12} key={fieldOption.value}>
											<Grid container direction="column">
												<Grid item xs={12}>
													<Typography className={classes.searchFieldLabel}>
														{fieldOption.label}
													</Typography>
												</Grid>
												<Grid item xs={12}>
													<Autocomplete
														value={selectedCustomField}
														options={customFieldOptions}
														getOptionSelected={(option, value) => option?.customFieldId === value?.customFieldId}
														getOptionLabel={(option) => option?.name || "Nenhum campo selecionado"}
														onChange={(_, selected) => handleChangeSearchCustomField(fieldOption.value, selected)}
														onOpen={async () => {
															await chatBotFlowConstructorStore.loadSpecificConstructionResources("saveInputLocationOptions")
														}}
														renderInput={(params) => (
															<TextField
																{...params}
																variant="outlined"
																placeholder="Nenhum campo selecionado"
																margin="none"
															/>
														)}
														size="small"
													/>
												</Grid>
											</Grid>
										</Grid>
									)
								})}
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default SearchSectionEditor
